import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import {
  FullMatchesPaginatedParams,
  getFullMatches,
  getFullMatchesPaginated,
} from "@/api/matches";
import { ExtractNextPageParam } from "@/tanstackQuery/helpers/extractNextPageParam";
import { MatchesQueryKeys } from "./matchesQueryKeys";

interface Options {}

export const useFullMatches = (options: Options) => {
  return useQuery({
    queryKey: [MatchesQueryKeys.FullMatches],
    enabled: true,

    queryFn: async ({ signal }) => {
      return await getFullMatches({ params: options, signal });
    },
  });
};

// Server-side paginated matches for the admin matches-list page.
export const useFullMatchesPaginated = (
  params: FullMatchesPaginatedParams,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [MatchesQueryKeys.FullMatchesPaginated, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) =>
      getFullMatchesPaginated({ params, signal }),
  });
};

// Same endpoint as `useFullMatchesPaginated`, accumulated page by page for a
// scroll-to-load list (the zero results board). `page` is owned by the query,
// so callers pass only the filters and the page size.
export const useFullMatchesInfinite = (
  params: Omit<FullMatchesPaginatedParams, "page">,
  options: { enabled?: boolean } = {}
) => {
  return useInfiniteQuery({
    queryKey: [MatchesQueryKeys.FullMatchesInfinite, params],
    enabled: options.enabled ?? true,
    initialPageParam: 1,
    getNextPageParam: ExtractNextPageParam.fromPaginatedResponse,
    queryFn: async ({ pageParam, signal }) =>
      getFullMatchesPaginated({ params: { ...params, page: pageParam }, signal }),
  });
};
